import { ApyAreaChart } from '@/components/charts/apyAreaChart'
import { DefinitionStat } from '@/components/common/definitionStat'
import { Tooltip, TooltipContent } from '@/components/common/tooltip'
import { InfoIcon } from '@/components/icons/info'
import { ApyRangeSettingsSelector } from '@/components/shared/apyRangeSettingsSelector'
import type { HistoricalData } from '@/queries/historical.query'
import type { VaultApyRange } from '@/types/apy'
import type { Vault } from '@/types/vault'
import { getApyCopy } from '@/utils/apy'
import { getValueColor } from '@/utils/styles/getValueColor'
import { checkActiveRewards } from '@/utils/vault/rewards'
import { formatDecimalToPercentage } from '@vaultsfyi/common/utils'
import type { ReactNode } from 'react'
import { TabHeader } from './tabHeader'

interface Props {
  vault: Vault
  apyRange: VaultApyRange
  includeRewards: boolean
  intrinsicApy: number | null
  chartData: HistoricalData
  rangeSelector?: ReactNode
  chartClassName?: string
  showBrush?: boolean
}

export const YieldPerformance = ({
  vault,
  apyRange,
  includeRewards,
  intrinsicApy,
  chartData,
  rangeSelector,
  chartClassName,
  showBrush = false,
}: Props) => {
  const { apy, rewards, additionalIncentives, apyComposite, asset } = vault
  const hasRewards = checkActiveRewards(rewards)
  const stats = (
    <dl className="flex max-w-full shrink-0 flex-wrap gap-x-8 gap-y-3">
      <DefinitionStat
        label="Total yield"
        value={getApyCopy({ apy, apyRange, includeRewards, apyComposite })}
        className="gap-0.5"
        labelClassName="font-normal text-body-foreground"
      />
      <DefinitionStat
        label="Base"
        value={formatDecimalToPercentage(apy[apyRange].base)}
        className="gap-0.5"
        labelClassName="font-normal text-body-foreground"
        valueClassName={getValueColor(apy[apyRange].base)}
      />
      {apyComposite && intrinsicApy != null && (
        <DefinitionStat
          label={
            <>
              <span>Intrinsic</span>
              <Tooltip className="rounded-full">
                <InfoIcon size={16} />
                <TooltipContent className="max-w-52">
                  <p className="font-semibold">Intrinsic APY</p>
                  <p>
                    {asset.symbol} APY = {formatDecimalToPercentage(intrinsicApy)}
                  </p>
                  <p className="mt-1">
                    Yield intrinsic to the underlying vault asset, such as staking yield or external rewards.
                  </p>
                </TooltipContent>
              </Tooltip>
            </>
          }
          className="gap-0.5"
          labelClassName="flex items-center gap-1 font-normal text-[rgb(var(--chart-cyan))]"
          value={formatDecimalToPercentage(intrinsicApy)}
          valueClassName="text-[rgb(var(--chart-cyan))]"
        />
      )}
      {hasRewards && (
        <DefinitionStat
          label="Rewards"
          value={formatDecimalToPercentage(apy[apyRange].reward)}
          className="gap-0.5"
          labelClassName="font-normal text-[rgb(var(--chart-violet))]"
          valueClassName="text-[rgb(var(--chart-violet))]"
        />
      )}
      {additionalIncentives && (
        <DefinitionStat
          label="Points"
          value={additionalIncentives}
          className="gap-0.5"
          labelClassName="font-normal text-accent"
          valueClassName="text-accent"
        />
      )}
    </dl>
  )
  const apyRangeSelector = (
    <div className="flex min-w-0 flex-col items-start gap-1 text-sm">
      <p className="ml-1 text-xs">Show APY as avg. of</p>
      <ApyRangeSettingsSelector className="h-6 p-0.5" />
    </div>
  )

  return (
    <div className="flex flex-col gap-4 pt-2">
      <TabHeader
        title="Yield - detailed view"
        description="Historical annualized return, based on the vault's share price change and any rewards accrued during the period."
      />
      {rangeSelector ? (
        <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
          {stats}
          <div className="flex max-w-full shrink-0 flex-wrap items-end gap-x-8 gap-y-3">
            {apyRangeSelector}
            {rangeSelector}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {stats}
          {apyRangeSelector}
        </div>
      )}
      <ApyAreaChart data={chartData} includeRewards={includeRewards} className={chartClassName} showBrush={showBrush} />
    </div>
  )
}
